import { API_BASE, authFetch, readJson } from "./base";
import type {
  PlanDiffResult,
  PlanDiffResponse,
  PlanDraftBlock,
  PlanLifeAreaBreakdown,
  PlanProjection,
} from "./projection-types";

export async function getPlanProjection(date?: string): Promise<PlanProjection> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  const response = await authFetch(`${API_BASE}/projections/plan${suffix}`);
  return readJson<PlanProjection>(response, "Failed to load plan projection");
}

export async function generatePlanDiff(body: {
  date?: string;
  preferences?: Record<string, unknown>;
  commitment_level?: "must_do" | "planned" | "flexible";
  /**
   * Per-area weekly minute caps (FR-1, FR-18). Forwarded through plan/diff
   * into the inner generate_schedule call so the closed loop's deterministic
   * cap fires for the AI Time Box preview. 0 means "do not schedule".
   */
  life_area_commits?: Record<string, number>;
} = {}, options: { signal?: AbortSignal } = {}): Promise<PlanDiffResult> {
  const response = await authFetch(`${API_BASE}/plan/diff`, {
    method: "POST",
    signal: options.signal,
    body: JSON.stringify(body),
  });
  return readJson<PlanDiffResult>(response, "Failed to generate AI plan");
}

export async function applyPlanDiff(body: {
  date: string;
  commitment_level: "must_do" | "planned" | "flexible";
  blocks: PlanDraftBlock[];
  summary: string;
  focus_block_id?: string | null;
  total_focus_minutes: number;
  total_break_minutes: number;
  life_area_breakdown: PlanLifeAreaBreakdown[];
  draft_id?: string | null;
  protected_block_ids?: string[];
  dropped_block_ids?: string[];
  idempotency_key?: string | null;
}): Promise<PlanDiffResponse> {
  const { idempotency_key, ...payload } = body;
  const response = await authFetch(`${API_BASE}/plan/apply`, {
    method: "POST",
    headers: idempotency_key ? { "Idempotency-Key": idempotency_key } : undefined,
    body: JSON.stringify(payload),
  });
  const applied = await readJson<Omit<PlanDiffResponse, "status" | "adds" | "moves" | "untouched">>(
    response,
    "Failed to apply AI plan"
  );
  return {
    status: "ready",
    ...applied,
    adds: [],
    moves: [],
    untouched: [],
  };
}
